/**
 * Observe feed-chip grouping — honesty only.
 *
 * API Observe paints one chip per `api_ingest_pipelines` row, labeled by
 * `api_name`. Repeated Open-Meteo / Tracking Company registrations therefore
 * look identical (stale + healthy mixed, no pipe id). This groups near-
 * duplicates that share a provider family (same provider + endpoint, or the
 * same pipeline family when endpoint aliases differ). Pipelines are not
 * deleted; the healthiest member stays the face of the group.
 *
 * Four Weather providers stay four groups — family keys are provider-scoped,
 * never a shared "weather" bucket.
 */

import type { OpsStatusPipeline, PipelineHealth } from '../../features/dataflow/types';

const HEALTH_RANK: Record<string, number> = {
  healthy: 0,
  degraded: 1,
  stale: 2,
  error: 3,
};

export type ObserveFeedChipGroup = {
  familyKey: string;
  label: string;
  representative: OpsStatusPipeline;
  members: OpsStatusPipeline[];
  distinguisher: {
    pipeline_id: string;
    endpoint_id: string | null;
    last_success_at: string | null;
    member_count: number;
    healthy_count: number;
  };
};

export function slugObserveToken(value: unknown): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Drop the registration suffix `_<6+ hex>` used on minted api_ids. */
export function stripRegistrationSuffix(apiId: unknown): string {
  return String(apiId || '').trim().replace(/_[0-9a-f]{6,}$/i, '');
}

/**
 * Provider family — `open-meteo_da318d11` and `Open-Meteo Weather Forecast API`
 * share `open-meteo`. Does not collapse AfriGIS / MET / Google into one key.
 */
export function observeProviderFamilyKey(pipe: Pick<OpsStatusPipeline, 'api_id' | 'api_name'>): string {
  const fromId = slugObserveToken(
    stripRegistrationSuffix(pipe.api_id).replace(/^api[-_]/i, '')
  );
  const fromName = slugObserveToken(pipe.api_name);

  if (fromId && fromName) {
    if (fromName === fromId || fromName.startsWith(`${fromId}-`) || fromId.startsWith(`${fromName}-`)) {
      return fromId.length <= fromName.length ? fromId : fromName;
    }
    return fromId;
  }
  return fromId || fromName || 'unknown';
}

export function observeEndpointKey(pipe: Pick<OpsStatusPipeline, 'endpoint_id'>): string {
  return slugObserveToken(pipe.endpoint_id);
}

/** Family key: provider family (pipeline family). Same Open-Meteo aliases share one key. */
export function observeFeedFamilyKey(pipe: Pick<OpsStatusPipeline, 'api_id' | 'api_name' | 'endpoint_id'>): string {
  return observeProviderFamilyKey(pipe);
}

export function shortPipeId(pipelineId: unknown): string {
  const raw = String(pipelineId || '').trim();
  if (!raw) return '—';
  const uuid = raw.match(/^(pipe_)?([0-9a-f]{8})/i);
  if (uuid) return `${uuid[1] || ''}${uuid[2]}`;
  return raw.length > 16 ? `${raw.slice(0, 12)}…` : raw;
}

function healthRank(health: PipelineHealth | string | null | undefined): number {
  return HEALTH_RANK[String(health || '')] ?? 9;
}

function successMs(iso: string | null | undefined): number {
  if (!iso) return -1;
  const ms = new Date(iso).getTime();
  return Number.isFinite(ms) ? ms : -1;
}

/** Healthiest, then newest last success, then most successes — never prefer stale over healthy. */
export function pickObserveFeedRepresentative(members: OpsStatusPipeline[]): OpsStatusPipeline {
  if (members.length === 0) {
    throw new Error('pickObserveFeedRepresentative: empty group');
  }
  return [...members].sort((a, b) => {
    const health = healthRank(a.health) - healthRank(b.health);
    if (health !== 0) return health;
    const success = successMs(b.last_success_at) - successMs(a.last_success_at);
    if (success !== 0) return success;
    const counts = (b.success_count || 0) - (a.success_count || 0);
    if (counts !== 0) return counts;
    return String(a.pipeline_id || '').localeCompare(String(b.pipeline_id || ''));
  })[0];
}

export function groupObserveFeedChips(pipelines: OpsStatusPipeline[]): ObserveFeedChipGroup[] {
  const buckets = new Map<string, OpsStatusPipeline[]>();
  for (const pipe of pipelines) {
    const key = observeFeedFamilyKey(pipe);
    const list = buckets.get(key) || [];
    list.push(pipe);
    buckets.set(key, list);
  }

  const groups: ObserveFeedChipGroup[] = [];
  for (const [familyKey, members] of buckets) {
    const representative = pickObserveFeedRepresentative(members);
    const healthyCount = members.filter((m) => m.health === 'healthy').length;
    groups.push({
      familyKey,
      label: representative.api_name || representative.endpoint_id || representative.pipeline_id || 'feed',
      representative,
      members,
      distinguisher: {
        pipeline_id: representative.pipeline_id || '',
        endpoint_id: representative.endpoint_id || null,
        last_success_at: representative.last_success_at || null,
        member_count: members.length,
        healthy_count: healthyCount,
      },
    });
  }

  return groups.sort((a, b) => {
    const health = healthRank(a.representative.health) - healthRank(b.representative.health);
    if (health !== 0) return health;
    return a.label.localeCompare(b.label);
  });
}

export function observeFeedChipTitle(group: ObserveFeedChipGroup): string {
  const lines = group.members.map((m) => {
    const pipe = m.pipeline_id || '—';
    const endpoint = m.endpoint_id || 'no-endpoint';
    const success = m.last_success_at || 'never';
    return `${pipe} · ${endpoint} · ${m.health || m.status} · last success ${success}`;
  });
  const head = group.members.length > 1
    ? `${group.label} — ${group.members.length} pipes in this family (showing the healthiest)`
    : group.label;
  return [head, ...lines].join('\n');
}
